import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Camera, Loader2, RefreshCw, IdCard, User, ShieldCheck, ShieldAlert, Maximize2, PenLine, Download } from 'lucide-react';
import { getGuestKycStatus, uploadGuestKyc, getGuestKycImageBlob, getGuestDetailsPdf } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { SignaturePad } from './SignaturePad';

// Target sizes for compression (max dimension, jpeg quality) — keeps files ~30-70KB
const SLOTS = {
  photo:    { label: 'Guest Photo',   maxW: 480, maxH: 640, quality: 0.72 },
  id_front: { label: 'ID Proof - Front', maxW: 1000, maxH: 700, quality: 0.72 },
  id_back:  { label: 'ID Proof - Back',  maxW: 1000, maxH: 700, quality: 0.72 },
};

const STATUS_STYLES = {
  booked: 'bg-blue-100 text-blue-700',
  new_join: 'bg-emerald-100 text-emerald-700',
  occupied: 'bg-slate-100 text-slate-700',
  notice: 'bg-amber-100 text-amber-700',
  shifting: 'bg-purple-100 text-purple-700',
  left: 'bg-rose-100 text-rose-700',
  joined: 'bg-emerald-100 text-emerald-700',
  day_basis: 'bg-purple-100 text-purple-700',
  overstay: 'bg-red-500 text-white',
};
const STATUS_LABELS = {
  booked: 'Booked', new_join: 'Joined', occupied: 'Occupied', notice: 'Notice',
  shifting: 'Shifting', left: 'Left', joined: 'Active', day_basis: 'Day Basis', overstay: 'Overstay',
};

const fmtDate = (iso) => {
  if (!iso) return null;
  const out = formatDate(iso);
  return out === '-' ? null : out;
};

const DetailRow = ({ label, value, mono, accent }) => (
  <div className="flex items-baseline gap-2 py-0.5" data-testid={`profile-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <span className="text-[13px] text-slate-400 w-24 shrink-0">{label}</span>
    <span className={`text-[13px] font-medium min-w-0 truncate ${accent ? 'text-indigo-600 font-mono font-semibold' : mono ? 'text-slate-800 font-mono' : 'text-slate-800'}`}>{value || '—'}</span>
  </div>
);

export const GuestKYC = ({ guestId, guest = {}, canEdit = true }) => {
  const [status, setStatus] = useState({});
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [cameraSlot, setCameraSlot] = useState(null);
  const [camReady, setCamReady] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, label }
  const [sigOpen, setSigOpen] = useState(false);
  const [sigSaving, setSigSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const loadImage = useCallback(async (docType, version) => {
    try {
      const res = await getGuestKycImageBlob(guestId, docType, version);
      const url = URL.createObjectURL(res.data);
      setUrls((u) => ({ ...u, [docType]: url }));
    } catch (e) { /* not present */ }
  }, [guestId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getGuestKycStatus(guestId);
      setStatus(data);
      for (const dt of Object.keys(SLOTS)) {
        if (data[dt]?.present) loadImage(dt, data[dt]?.uploaded_at);
      }
      if (data.signature?.present) loadImage('signature', data.signature?.uploaded_at);
    } catch (e) {
      toast.error('Failed to load KYC documents');
    } finally {
      setLoading(false);
    }
  }, [guestId, loadImage]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => () => { Object.values(urls).forEach((u) => URL.revokeObjectURL(u)); }, []); // eslint-disable-line

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCamReady(false);
  }, []);

  const openCamera = async (slot) => {
    setCameraSlot(slot);
    setCamReady(false);
    try {
      const facing = 'environment';
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamReady(true);
    } catch (e) {
      toast.error('Cannot access camera. Please allow camera permission.');
      setCameraSlot(null);
    }
  };

  const closeCamera = () => { stopCamera(); setCameraSlot(null); };

  const capture = async () => {
    const slot = cameraSlot;
    const cfg = SLOTS[slot];
    const video = videoRef.current;
    if (!video) return;
    const vw = video.videoWidth, vh = video.videoHeight;
    let tw = vw, th = vh;
    const ratio = Math.min(cfg.maxW / vw, cfg.maxH / vh, 1);
    tw = Math.round(vw * ratio); th = Math.round(vh * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = tw; canvas.height = th;
    canvas.getContext('2d').drawImage(video, 0, 0, tw, th);
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', cfg.quality));
    if (!blob) { toast.error('Capture failed'); return; }
    closeCamera();
    setUploading(slot);
    try {
      const res = await uploadGuestKyc(guestId, slot, blob);
      toast.success(`${cfg.label} saved (${Math.round((res.data.size || blob.size) / 1024)} KB)`);
      if (urls[slot]) URL.revokeObjectURL(urls[slot]);
      setUrls((u) => { const n = { ...u }; delete n[slot]; return n; });
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const saveSignature = async (blob) => {
    setSigSaving(true);
    try {
      await uploadGuestKyc(guestId, 'signature', blob);
      if (urls.signature) URL.revokeObjectURL(urls.signature);
      setUrls((u) => { const n = { ...u }; delete n.signature; return n; });
      toast.success('Signature saved');
      setSigOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save signature');
    } finally {
      setSigSaving(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await getGuestDetailsPdf(guestId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Guest_Registration_${(guest.name || 'Guest').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Failed to generate PDF';
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) {}
      }
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const allPresent = ['photo', 'id_front', 'id_back'].every((s) => status[s]?.present);
  const hasSignature = !!status.signature?.present;
  const photoUrl = urls.photo;
  const st = guest.status || 'joined';

  // ID document slot (front / back)
  const DocSlot = ({ slot }) => {
    const cfg = SLOTS[slot];
    const has = status[slot]?.present;
    const url = urls[slot];
    const busy = uploading === slot;
    const uploaded = fmtDate(status[slot]?.uploaded_at);
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden" data-testid={`kyc-slot-${slot}`}>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 px-3 pt-3 pb-2">
          <IdCard className="h-3.5 w-3.5 text-indigo-500" /> {cfg.label}
        </div>
        <button
          type="button"
          onClick={() => url && setLightbox({ url, label: cfg.label })}
          className={`group relative block w-full bg-white aspect-[16/10] overflow-hidden ${url ? 'cursor-zoom-in' : 'cursor-default'}`}
        >
          {busy ? (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : url ? (
            <>
              <img src={url} alt={cfg.label} className="w-full h-full object-cover" data-testid={`kyc-img-${slot}`} />
              <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/30 transition-colors flex items-center justify-center">
                <Maximize2 className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </>
          ) : has ? (
            <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 gap-1">
              <IdCard className="h-8 w-8" />
              <span className="text-[10px]">Not captured</span>
            </div>
          )}
        </button>
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <span className="text-[11px] text-slate-400 truncate">
            {uploaded ? `Uploaded on : ${uploaded}` : 'Not uploaded'}
          </span>
          {canEdit && (
            <div className="flex gap-1 shrink-0">
              <Button type="button" size="sm" variant={has ? 'outline' : 'default'} className="h-7 text-[11px] px-2" onClick={() => openCamera(slot)} disabled={busy} data-testid={`kyc-capture-${slot}`}>
                {has ? <RefreshCw className="h-3 w-3 mr-1" /> : <Camera className="h-3 w-3 mr-1" />}
                {has ? 'Retake' : 'Capture'}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="guest-profile-card">
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <>
          {/* Top: photo (left) + details (right) */}
          <div className="flex gap-4 p-4">
            {/* Photo */}
            <div className="relative shrink-0 w-[128px]">
              <button
                type="button"
                onClick={() => photoUrl && setLightbox({ url: photoUrl, label: 'Guest Photo' })}
                className={`w-full aspect-[3/4] rounded-xl border border-slate-200 bg-slate-100 overflow-hidden shadow-sm ${photoUrl ? 'cursor-zoom-in' : ''}`}
                data-testid="profile-avatar"
              >
                {uploading === 'photo' ? (
                  <div className="h-full w-full flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
                ) : photoUrl ? (
                  <img src={photoUrl} alt="Guest" className="h-full w-full object-cover" data-testid="profile-avatar-img" />
                ) : (
                  <div className="h-full w-full flex flex-col items-center justify-center text-slate-300 gap-1">
                    <User className="h-10 w-10" />
                    <span className="text-[10px]">No photo</span>
                  </div>
                )}
              </button>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => openCamera('photo')}
                  className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-7 px-2.5 rounded-full bg-indigo-600 text-white text-[11px] font-medium flex items-center gap-1 shadow hover:bg-indigo-700 transition-colors"
                  title={status.photo?.present ? 'Retake photo' : 'Take photo'}
                  data-testid="profile-avatar-capture"
                >
                  {status.photo?.present ? <RefreshCw className="h-3 w-3" /> : <Camera className="h-3 w-3" />}
                  {status.photo?.present ? 'Retake' : 'Photo'}
                </button>
              )}
            </div>

            {/* Details */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="text-xl font-bold text-slate-900 truncate">{guest.name || 'Guest'}</h3>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[st] || 'bg-slate-100 text-slate-700'}`} data-testid="profile-status-badge">
                  {STATUS_LABELS[st] || st}
                </span>
              </div>
              <div className="space-y-0.5">
                <DetailRow label="Guest ID" value={guest.guest_code} accent />
                <DetailRow label="Mobile" value={guest.mobile} mono />
                <DetailRow label="Emergency" value={guest.emergency_contact} mono />
                <DetailRow label="Property" value={guest.property_name} />
                <DetailRow label="Room" value={guest.room_info} mono />
                <DetailRow label="Sharing" value={guest.sharing_type} />
                <DetailRow label="Joining" value={formatDate(guest.joining_date)} mono />
              </div>
            </div>
          </div>

          {/* KYC documents */}
          <div className="px-4 pb-4 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-indigo-600 tracking-wide">KYC DOCUMENTS</h4>
              {allPresent ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="kyc-status-badge">
                  Complete <ShieldCheck className="h-4 w-4" />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="kyc-status-badge">
                  Pending <ShieldAlert className="h-4 w-4" />
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DocSlot slot="id_front" />
              <DocSlot slot="id_back" />
            </div>
          </div>

          {/* Signature + Registration PDF */}
          <div className="px-4 pb-4 pt-3 border-t border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-indigo-600 tracking-wide">GUEST SIGNATURE</h4>
              {hasSignature ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="signature-status-badge">
                  Signed <ShieldCheck className="h-4 w-4" />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="signature-status-badge">
                  Not signed <ShieldAlert className="h-4 w-4" />
                </span>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden">
              <div className="bg-white flex items-center justify-center h-24">
                {urls.signature ? (
                  <img src={urls.signature} alt="Signature" className="max-h-24 max-w-full object-contain" data-testid="signature-preview" />
                ) : (
                  <span className="text-xs text-slate-300">No signature captured</span>
                )}
              </div>
              {canEdit && (
                <div className="px-3 py-2">
                  <Button type="button" size="sm" variant={hasSignature ? 'outline' : 'default'} className="h-8 text-xs w-full" onClick={() => setSigOpen(true)} data-testid="signature-open-btn">
                    <PenLine className="h-3.5 w-3.5 mr-1" /> {hasSignature ? 'Re-sign' : 'Add Signature'}
                  </Button>
                </div>
              )}
            </div>

            <Button
              type="button"
              className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700"
              onClick={downloadPdf}
              disabled={!hasSignature || downloading}
              title={!hasSignature ? 'Capture the guest signature first' : 'Download the 2-page registration form'}
              data-testid="download-registration-pdf-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download Registration Form (PDF)
            </Button>
            {!hasSignature && (
              <p className="text-[11px] text-amber-600 text-center mt-1.5">Signature required before download</p>
            )}
          </div>
        </>
      )}

      {/* Signature pad */}
      <SignaturePad open={sigOpen} onOpenChange={setSigOpen} onSave={saveSignature} saving={sigSaving} />

      {/* Camera dialog */}
      <Dialog open={!!cameraSlot} onOpenChange={(o) => { if (!o) closeCamera(); }}>
        <DialogContent className="max-w-md" data-testid="kyc-camera-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Camera className="h-4 w-4" /> {cameraSlot ? SLOTS[cameraSlot].label : ''}</DialogTitle>
          </DialogHeader>
          <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3]">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            {!camReady && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-white/70" /></div>}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={closeCamera}>Cancel</Button>
            <Button type="button" className="flex-1" onClick={capture} disabled={!camReady} data-testid="kyc-shutter-btn">
              <Camera className="h-4 w-4 mr-1" /> Capture
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen image viewer */}
      <Dialog open={!!lightbox} onOpenChange={(o) => { if (!o) setLightbox(null); }}>
        <DialogContent className="max-w-3xl p-2 bg-slate-900 border-slate-800" data-testid="kyc-lightbox">
          <DialogHeader className="px-2 pt-1">
            <DialogTitle className="text-slate-100 text-sm">{lightbox?.label}</DialogTitle>
          </DialogHeader>
          {lightbox && (
            <img src={lightbox.url} alt={lightbox.label} className="w-full max-h-[75vh] object-contain rounded-lg" data-testid="kyc-lightbox-img" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestKYC;
